import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Buttons row, rendered in a sticky footer. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** 'center' = dialog, 'right' = drawer. Prefer <Drawer>. */
  placement?: 'center' | 'right';
  /** Disable Esc / backdrop close (e.g. while saving). Default true. */
  dismissible?: boolean;
  /** Element to focus on open (defaults to the first focusable control). */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
}

/** Open modals, oldest first: only the topmost one reacts to Esc / Tab (confirm dialogs sit on top of forms). */
const modalStack: symbol[] = [];

const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' } as const;

/**
 * Accessible modal: role=dialog + aria-modal, focus trap, Esc closes, backdrop click closes,
 * focus returns to the opener, page scroll locked. Rendered in a portal.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  placement = 'center',
  dismissible = true,
  initialFocusRef,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const me = Symbol('modal');
    modalStack.push(me);
    const opener = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const panel = panelRef.current;
    const focusables = () => Array.from(panel?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const first =
      initialFocusRef?.current ??
      panel?.querySelector<HTMLElement>('[data-autofocus]') ??
      focusables().find((el) => !el.hasAttribute('data-modal-close')) ??
      panel;
    first?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (modalStack[modalStack.length - 1] !== me) return;
      if (e.key === 'Escape' && dismissible) {
        e.stopPropagation();
        onCloseRef.current();
      } else if (e.key === 'Tab') {
        const els = focusables();
        if (els.length === 0) {
          e.preventDefault();
          return;
        }
        const firstEl = els[0];
        const lastEl = els[els.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      const at = modalStack.indexOf(me);
      if (at >= 0) modalStack.splice(at, 1);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [open, dismissible, initialFocusRef]);

  if (!open) return null;

  const drawer = placement === 'right';
  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex bg-overlay',
        drawer ? 'justify-end' : 'items-center justify-center p-4',
      )}
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cn(
          'flex flex-col bg-surface text-fg shadow-pop outline-none',
          drawer
            ? 'h-full w-full max-w-md border-l border-border'
            : cn('max-h-[90vh] w-full rounded-xl border border-border', widths[size]),
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-semibold">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-0.5 text-sm text-fg-muted">
                {description}
              </p>
            )}
          </div>
          {dismissible && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Close dialog"
              data-modal-close
              onClick={onClose}
            >
              <X className="size-4" />
            </Button>
          )}
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-surface-2 px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Side panel variant of Modal (forms that benefit from seeing the list behind). */
export function Drawer(props: Omit<ModalProps, 'placement' | 'size'>) {
  return <Modal {...props} placement="right" />;
}
